import Service from '@ember/service';

import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import { toArray } from 'rise-ui/utils/array.js';
import { maxDate, minDate } from 'rise-ui/config/time-series/query-params.js';
import { formatDate, parseDate } from 'rise-ui/utils/date.js';
import { call } from 'rise-ui/utils/function.js';
import { get } from 'rise-ui/utils/object.js';
import { readOnly } from 'rise-ui/utils/object/computed.js';

const transformDateRanges = dates => {
  return dates?.length ? [{
    from: dates[0],
    to: dates[1]
  }] : undefined;
};

const transformDates = dateRanges => {
  const ranges = {};
  Object.keys(dateRanges || {}).forEach(key => {
    ranges[key] = transformDateRanges(dateRanges[key]);
  });
  return ranges;
};

export default class TimeSeriesDatesService extends Service {
  @service('time-series/selected-locations') timeSeriesLocations;
  @service('time-series/params') timeSeriesParams;
  @service('route/time-series') timeSeriesRoute;

  // Whether to add location/item/modelRun when date is set
  addByDate = true;

  @cached get defaultDates() {
    return transformDateRanges(this.timeSeriesParams.defaultDates);
  }
  @cached get itemDates() {
    return transformDates(this.timeSeriesParams.itemDates);
  }
  @cached get modelRunDates() {
    return transformDates(this.timeSeriesParams.modelRunDates);
  }

  setDefaultDates(dates) {
    return (params) => {
      params.defaultDateRanges = dates;
      return params;
    };
  }
  clearSetDates(prop, id) {
    return params => {
      if (params[prop].has(id)) {
        params[prop].delete(id);
      }
      return params;
    };
  }

  clearItemDates(itemId) {
    return this.clearSetDates('itemDateRanges', itemId);
  }
  setItemDates(itemId) {
    return (dates)  => {
      return (params) => {
        if (this.addByDate) {
          params.items.add(itemId);
        }
        params.itemDateRanges.set(itemId, dates);
        return params;
      };
    };
  }
  clearModelRunDates(modelRunId) {
    return this.clearSetDates('modelRunDateRanges', modelRunId);
  }
  setModelRunDates(modelRunId) {
    return dates => {
      return params => {
        if (this.addByDate) {
          params.modelRuns.add(modelRunId);
        }
        params.modelRunDateRanges.set(modelRunId, dates);
        return params;
      };
    };
  }

  get getSelectionLink() {
    return this.timeSeriesRoute.getLink;
  }

  setDatesRanges(dateRange, fromTo, value, setDates, allowMaxRange) {
    const allowBothMax = allowMaxRange === true || ['both','all'].includes(allowMaxRange);
    const allowOneMax = allowBothMax || allowMaxRange
      && !['none','no','false'].includes(allowMaxRange);
    dateRange = dateRange || {};
    let [ from, to ] = [ [ 'from', minDate ], [ 'to', maxDate ] ]
      .map(([ key, max ]) => {
        let date = fromTo === key ? value : dateRange[key] !== max && dateRange[key];
        return formatDate(parseDate(date), 'url');
      });
    if (!from || !to) {
      if (allowBothMax || allowOneMax && (from || to)) {
        from = from || minDate;
        to = to || maxDate;
      }
    }
    const link = call(this.getSelectionLink, null, setDates([
      from,
      to
    ]));
    link?.transition();
  }

  @action onDefaultDateChange(fromTo, value) {
    this.setDatesRanges(this.defaultDates?.[0], fromTo, value, this.setDefaultDates, 'one');
  }

  @action onItemDateChange(itemId, fromTo, value) {
    const dates = this.itemDates[itemId];
    this.setDatesRanges(dates?.[0], fromTo, value, this.setItemDates(itemId));
  }

  @action onModelRunDateChange(modelRunId, fromTo, value) {
    const dates = this.modelRunDates[modelRunId];
    this.setDatesRanges(dates?.[0], fromTo, value, this.setModelRunDates(modelRunId));
  }
}
